/**
 * Module dependencies.
 */

import {
  JSX,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Language } from 'types/language';
import { MobileMenu } from 'components/molecules/mobile-menu/mobile-menu';
import { useBreakpoints } from 'components/hooks/use-breakpoints';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
};

/**
 * `MenuContextType` type.
 */

type MenuContextType = {
  isMenuOpen?: boolean;
  setIsMenuOpen?: (isOpen: boolean) => void;
  setLanguages?: (languages: Language[]) => void;
};

/**
 * `MenuContext` context.
 */

const MenuContext = createContext<MenuContextType>({});

/**
 * Use menu context.
 */

export function useMenuContext() {
  return useContext(MenuContext);
}

/**
 * Export `MenuProvider` component.
 */

export function MenuProvider({ children }: Props): JSX.Element {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { breakpoint } = useBreakpoints();

  useEffect(() => {
    if (isMenuOpen) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <MenuContext.Provider
      value={{
        isMenuOpen,
        setIsMenuOpen,
        setLanguages,
      }}
    >
      <>
        {children}

        {['mobile', 'mobileLarge', 'tablet'].includes(breakpoint) && (
          <MobileMenu
            isOpen={isMenuOpen}
            languages={languages}
            setIsOpen={setIsMenuOpen}
          />
        )}
      </>
    </MenuContext.Provider>
  );
}
